/**
 * How one Postgres cell is drawn, and how a page of them leaves as a CSV.
 *
 * Carried over from the Agent V5 Database tab
 * (`back4app-frontend`: `modules/agents/components/panels/database/renderCell.tsx`,
 * `QueryConsole/resultsTable.tsx`, `csvExport.ts`) — the decisions, not the
 * code: that grid is typed by the Parse schema (pointers, relations, ACLs,
 * files) and styled by the agent dashboard's own `.db-table` stylesheet, and
 * neither exists here. What came across is what survives the move to raw
 * Postgres:
 *
 *   - NULL drawn as the word, so an empty string and a missing value never
 *     look alike;
 *   - the column's type printed beside its name, small and dim, so a grid of
 *     numbers says which are `int4` and which are `numeric`;
 *   - `title` on every cell, capped, so a long value is readable on hover
 *     without the column growing to fit it;
 *   - a fixed width per column, from its type and header, with the grid
 *     scrolling sideways, rather than columns that resize themselves per page
 *     of data.
 *
 * The CSV is written here rather than through PapaParse (which the agent
 * dashboard has and this one does not) — RFC 4180 quoting is the whole job.
 */

/** Cap so one large JSON value can't build a megabyte-long tooltip. */
const MAX_TITLE_CHARS = 300;

/** Beyond this a cell is clipped in place; the full value stays in `title`. */
const MAX_CELL_CHARS = 160;

export function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

export function titleFor(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  const text = formatCell(value);

  if (!text) {
    return undefined;
  }

  return text.length > MAX_TITLE_CHARS
    ? `${text.slice(0, MAX_TITLE_CHARS)}…`
    : text;
}

/**
 * The short form of a Postgres type, for the badge under a column name.
 *
 * `character varying(255)` is the column's type but not what the header has
 * room for, and the length is not what the reader is checking at a glance —
 * they are checking whether it is text, a number, or a time.
 */
export function shortTypeName(dataType: string): string {
  const base = dataType.replace(/\(.*\)$/, '').trim();

  const SHORT: Record<string, string> = {
    'character varying': 'varchar',
    character: 'char',
    'timestamp with time zone': 'timestamptz',
    'timestamp without time zone': 'timestamp',
    'time with time zone': 'timetz',
    'time without time zone': 'time',
    'double precision': 'float8',
    integer: 'int4',
    smallint: 'int2',
    bigint: 'int8',
    boolean: 'bool',
  };

  const short = SHORT[base] || base;

  return dataType.endsWith('[]') && !short.endsWith('[]') ? `${short}[]` : short;
}

/** Right-align the types a reader compares by magnitude, never text. */
export function isNumericType(dataType: string): boolean {
  return /^(smallint|integer|bigint|numeric|decimal|real|double precision|money|int2|int4|int8|float4|float8)/.test(
    dataType
  );
}

/**
 * How wide a column is drawn, in pixels.
 *
 * From the TYPE, because the type says how long its values can be: an `int8`
 * id is a few digits, a timestamp is always the same seventeen characters, and
 * text is whatever someone wrote. One width for all of them made an id column
 * as wide as an e-mail column, with the number pushed to the far edge of it.
 *
 * Never narrower than its own header — the name and the sort arrow on one
 * line — so a short column does not truncate the name that says what it is.
 * Never from the data: a page of rows must not move the layout.
 */
export function columnWidth(column: {
  name: string,
  dataType: string,
}): number {
  const type = shortTypeName(column.dataType);

  // Every width below is the value that fits the type plus a hand's breadth:
  // read at a glance, a column whose text ends exactly at its edge looks cut
  // even when it is whole, and the grid scrolls sideways anyway.
  const byType = (() => {
    if (type.endsWith('[]')) return 240;
    if (/^bool/.test(type)) return 120;
    if (/^(int2|int4|smallint|integer|serial)/.test(type)) return 120;
    if (/^(int8|bigint|bigserial)/.test(type)) return 140;
    if (isNumericType(type)) return 156;
    // 36 characters of monospace and the cell's own padding: at 296 the last
    // group of a uuid was landing under the ellipsis.
    if (type === 'uuid') return 320;
    if (/^timestamp/.test(type)) return 210;
    if (/^date/.test(type)) return 140;
    if (/^time/.test(type)) return 128;
    if (/^json/.test(type)) return 320;
    if (/^(text|varchar|char|citext)/.test(type)) {
      // `char(3)` and `varchar(20)` say how long a value can get, and a
      // currency code does not need the room of a free-text column.
      const declared = Number(column.dataType.match(/\((\d+)\)$/)?.[1]);

      return declared ? Math.min(260, declared * 7.6 + 32) : 260;
    }
    return 220;
  })();

  // The name in monospace at 13px (~7.8px a character), the cell's padding,
  // and room for the sort arrow. The type is in the header's tooltip and on
  // Structure, not on the grid.
  const header = column.name.length * 7.8 + 28 + 18;

  return Math.round(Math.min(Math.max(byType, header), 420));
}

/** `public.orders` is written bare; anything else keeps its schema. */
export const qualifyName = (schema: string, name: string) =>
  schema === 'public' ? name : `${schema}.${name}`;

/**
 * An identifier as a statement needs it: bare when Postgres would read it
 * back unchanged, double-quoted otherwise — `createdAt` unquoted is folded to
 * `createdat` and names a column that does not exist.
 */
export const quoteIdent = (name: string) =>
  /^[a-z_][a-z0-9_$]*$/.test(name) ? name : `"${name.replace(/"/g, '""')}"`;

/** The table a generated statement writes to, quoted where it has to be. */
export const quotedTarget = (schema: string, name: string) =>
  schema === 'public' ? quoteIdent(name) : `${quoteIdent(schema)}.${quoteIdent(name)}`;

/**
 * A value as it would be typed into a statement — for the UPDATE the row
 * inspector writes out for editing. Quoted, with quotes doubled; JSON goes in
 * as its text, which Postgres casts to the column's json type on assignment.
 */
export function sqlLiteral(value: unknown): string {
  if (value === null || value === undefined) {
    return 'NULL';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  const text = typeof value === 'string' ? value : JSON.stringify(value);

  return `'${text.replace(/'/g, "''")}'`;
}

/** A yes/no column, which is read as a state and not as the word "true". */
export function isBooleanType(dataType: string): boolean {
  return /^bool/.test(dataType);
}

/** Types whose value is a moment, and is rendered as one. */
export function isTemporalType(dataType: string): boolean {
  return /^(timestamp|date)/.test(dataType);
}

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * `2026-09-11T02:40:26.726Z` → `2026-09-11 02:40Z`.
 *
 * Seconds and milliseconds are dropped from the GRID, not from the data: a
 * column of timestamps is read to see which row is newer, and three decimal
 * places of a second are twelve characters that never answer that. The full
 * value stays in the cell's title and in the CSV.
 *
 * UTC, always, with the Z that says so. A grid that renders each reader's own
 * timezone cannot be compared against anything the app itself logged, and a
 * time with no zone on it is a time nobody can check.
 *
 * `timestamp without time zone` has no zone to state, so it gets no Z — that
 * column IS ambiguous, and printing a Z on it would invent a fact.
 */
export function formatTemporal(value: unknown, dataType: string): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const day = `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())}`;

  if (/^date/.test(dataType)) {
    return day;
  }

  const clock = `${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}`;

  return `${day} ${clock}${/with time zone/.test(dataType) || dataType.endsWith('tz') ? 'Z' : ''}`;
}

/**
 * One cell, drawn as its type rather than as text.
 *
 * The grid is read by scanning a column, and what makes that possible is that
 * every value in it looks the same shape: numbers end at the same edge,
 * booleans are two states you tell apart without reading, and a timestamp is
 * the same width on every row. Printing `String(value)` for all of them was
 * one long ragged column of characters.
 *
 * NULL is drawn as the word, dim and italic — not as an em dash. A dash is a
 * value someone could have typed; `null` is the one thing it cannot be
 * confused with, and in a database the difference is a real one.
 */
export const CellValue = ({ value, dataType }: {
  value: unknown,
  dataType: string,
}) => {
  if (value === null || value === undefined) {
    return <span className="text-navy-400 italic">null</span>;
  }

  if (isBooleanType(dataType) && typeof value === 'boolean') {
    return (
      <span
        className={`inline-flex items-center rounded-control border px-1.5 py-px font-courrier text-ds-115 ${
          value
            ? 'border-state-ok/50 text-state-ok'
            : 'border-navy-500 text-navy-300'
        }`}
      >
        {String(value)}
      </span>
    );
  }

  if (isTemporalType(dataType)) {
    const moment = formatTemporal(value, dataType);

    if (moment) {
      return <span className="font-courrier whitespace-nowrap">{moment}</span>;
    }
  }

  return <>{clipCell(formatCell(value))}</>;
};

export function clipCell(text: string): string {
  return text.length > MAX_CELL_CHARS
    ? `${text.slice(0, MAX_CELL_CHARS)}…`
    : text;
}

// ─── CSV ────────────────────────────────────────────────────────────────────

function csvCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const text = typeof value === 'string' ? value : formatCell(value);

  // RFC 4180: quote when the value carries a comma, a quote or a newline,
  // and double any quote inside it.
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function rowsToCsv(columns: string[], rows: unknown[][]): string {
  return [columns.map(csvCell).join(','), ...rows.map((row) => row.map(csvCell).join(','))]
    .join('\r\n');
}

export function downloadCsv(filename: string, csv: string): void {
  // The BOM is what makes Excel read the file as UTF-8 instead of as the
  // local codepage — without it every accented value in an export opens
  // mangled, which is the single most reported thing about a CSV export.
  const blob = new Blob([`﻿${csv}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function formatBytes(bytes: number): string {
  if (!bytes) {
    return '0 B';
  }

  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / Math.pow(1024, exponent);

  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

/** `1234` → `1.2k`. The counts here are estimates; the digits would lie. */
export function formatApproxCount(count: number): string {
  if (count < 1000) {
    return String(count);
  }
  if (count < 1000000) {
    return `${(count / 1000).toFixed(count < 10000 ? 1 : 0)}k`;
  }

  return `${(count / 1000000).toFixed(1)}M`;
}
